import confetti from 'canvas-confetti';

// Stocker's real brand palette (violet/indigo/blue, plus a positive-green
// accent) -- matches the homepage gradient rather than generic rainbow
// confetti, so celebratory moments still look like this app.
const BRAND_COLORS = ['#a78bfa', '#818cf8', '#60a5fa', '#34d399'];

// Respects both OS-level detection AND the explicit, persisted
// Settings > Accessibility preference (App.js toggles the
// `reduce-motion-forced` class on <html> from that stored setting -- see
// index.css's "ACCESSIBILITY SETTINGS" section). A plain utility file, not
// a React component, so it reads the DOM class directly rather than
// needing Redux store access.
const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  (window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ||
    document.documentElement.classList.contains('reduce-motion-forced'));

/**
 * A small, quick burst from the top of the screen. For real "done!"
 * moments that don't happen on every click -- clearing notifications,
 * successfully joining a group -- not for routine likes/votes, which get
 * their own lighter pop animation instead (see index.css's .animate-pop).
 */
export const fireConfettiBurst = () => {
  if (prefersReducedMotion()) return;
  confetti({
    particleCount: 60,
    spread: 55,
    startVelocity: 35,
    origin: { y: 0.3 },
    colors: BRAND_COLORS,
    ticks: 150,
    scalar: 0.9,
  });
};

/**
 * A bigger, two-sided celebration for the rarest, most significant
 * moments -- a badge earned, a credibility tier-up. Fires from both
 * bottom corners for ~700ms.
 */
export const fireConfettiCelebration = () => {
  if (prefersReducedMotion()) return;
  const end = Date.now() + 700;
  const frame = () => {
    confetti({ particleCount: 4, angle: 60, spread: 60, origin: { x: 0, y: 0.6 }, colors: BRAND_COLORS });
    confetti({ particleCount: 4, angle: 120, spread: 60, origin: { x: 1, y: 0.6 }, colors: BRAND_COLORS });
    if (Date.now() < end) requestAnimationFrame(frame);
  };
  frame();
};

/**
 * A burst anchored to a specific DOM element (e.g. the bell icon a user
 * just clicked) instead of a generic screen position, so the celebration
 * visually originates from what the user actually interacted with.
 */
export const fireConfettiFromElement = (element) => {
  if (!element) return fireConfettiBurst();
  if (prefersReducedMotion()) return;
  const rect = element.getBoundingClientRect();
  const x = (rect.left + rect.width / 2) / window.innerWidth;
  const y = (rect.top + rect.height / 2) / window.innerHeight;
  confetti({
    particleCount: 45,
    spread: 50,
    startVelocity: 30,
    origin: { x, y },
    colors: BRAND_COLORS,
    ticks: 130,
    scalar: 0.8,
  });
};
